"use client";

import type { EcommerceProduct } from "@/shared";
import { useAuthInformation } from "@/shared";
import { useCallback, useEffect, useRef } from "react";

import {
  WISHLIST_INTENT_PARAM,
  useAddToWishlist,
} from "../../hooks/useAddToWishlist.hook";
import * as track from "../../tracking/wishlist.tracking";
import {
  AddToWishlistButton,
  AddToWishlistButtonProps,
} from "../AddToWishlistButton/AddToWishlistButton";

type Props = Pick<
  AddToWishlistButtonProps,
  "variant" | "className" | "filled" | "data-testid"
> & {
  product: EcommerceProduct;
};

const AddToWishlist = ({ product, ...props }: Props) => {
  const { isLoggedIn } = useAuthInformation();

  const {
    isWishlisted,
    toggleWishlisted,
    fallbackUrl,
    isLoading,
    isRequestPending,
  } = useAddToWishlist({
    sku: product.sku,
    enabled: isLoggedIn,
  });

  const hasResumedIntent = useRef(false);

  const handleToggleWishlisted = useCallback(() => {
    if (isRequestPending) return;

    if (isLoggedIn) {
      toggleWishlisted();

      if (isWishlisted) {
        track.removeFromWishlistClick({ product });
      } else {
        track.addToWishlistClick({ product });
      }

      return;
    }

    track.addToWishlistClick({ product });
  }, [isLoggedIn, isRequestPending, isWishlisted, product, toggleWishlisted]);

  useEffect(() => {
    if (!isLoggedIn || hasResumedIntent.current) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get(WISHLIST_INTENT_PARAM) !== product.sku) return;

    if (isLoading || isRequestPending) return;

    hasResumedIntent.current = true;

    params.delete(WISHLIST_INTENT_PARAM);
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`,
    );

    if (!isWishlisted) {
      handleToggleWishlisted();
    }
  }, [
    isLoggedIn,
    isLoading,
    isRequestPending,
    isWishlisted,
    product.sku,
    handleToggleWishlisted,
  ]);

  return (
    <AddToWishlistButton
      {...props}
      isWishlisted={isWishlisted || false}
      onClick={handleToggleWishlisted}
      isLoading={isLoading && isLoggedIn}
      href={isLoggedIn ? undefined : fallbackUrl}
    />
  );
};

export default AddToWishlist;
